/**
 * Campaigns — build, dry-run, start, pause, and what it actually did.
 *
 * **This is the only screen in the product where one click reaches every customer at once.** It
 * costs real money per message and it cannot be recalled: there is no un-send on WhatsApp, and a bad
 * send burns both the client's money and their number's quality rating with Meta. So the screen is
 * arranged around one idea — *the dry run is the product* — and everything else follows from it:
 *
 *  1. **Start is off until the dry run is of THIS definition and is still fresh.** The server
 *     decides that (`campaign.startable`), not this file, because a second implementation of that
 *     rule in a browser is a second thing that can be wrong. When it is off, the sentence beside it
 *     says which of the three reasons it is.
 *  2. **Every way the live send can differ from the dry run is printed ABOVE the Start button**, not
 *     in a tooltip and not afterwards. A promise of exactness the system cannot keep is worse than a
 *     caveat somebody read.
 *  3. **Every exclusion is a labelled row with a count.** "220 not sent" reads like a bug and gets
 *     raised as one; "90 have never given consent" is a sentence somebody can act on.
 *  4. **Results are split by whose failure it is** — ours or Meta's — because those two numbers
 *     reach two different people, and only one of them is an engineer.
 *
 * No customer's phone number or rendered message body appears anywhere on this page: the dry run
 * carries contact ids, languages, cities and counts, and nothing that could be quoted back.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '../components/Button.tsx';
import { Pill } from '../components/Pill.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { formatDateTime, formatTime } from '../lib/format.ts';
import { canManageCampaigns, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import {
  campaignsApi, exclusionLabel, messagingTierNote, rupees, WINDOW_REASONS,
  type Campaign, type CampaignDefinition, type CampaignsClient, type DryRun, type Facets,
  type ParamSpec, type Progress, type Weekday,
} from './campaignsApi.ts';

const INPUT = 'h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 text-sm text-fg disabled:opacity-50';
const DAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const STATUS_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'danger' | 'info'> = {
  draft: 'neutral', running: 'ok', paused: 'warn', done: 'info', cancelled: 'danger',
};

/** A blank campaign: every cap present, nothing guessed, the window inside business hours. */
const blank = (languages: string[]): CampaignDefinition => ({
  name: '',
  templateName: '',
  defaultLanguage: languages[0] ?? 'en',
  params: {},
  audience: {},
  citySequence: [],
  dailyCap: 250,
  window: { days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'], start: '10:00', end: '19:00' },
  pacePerSecond: 10,
});

export interface CampaignsPageProps {
  /** Injected in tests. The page is otherwise pointed at `/api/v1/campaigns`. */
  client?: CampaignsClient;
}

export function CampaignsPage({ client = campaignsApi }: CampaignsPageProps = {}): ReactNode {
  const qc = useQueryClient();
  const user = useUser();
  /**
   * Reading a campaign is for everybody; every button that does anything is `minRole: manager` at
   * the API. Below that the controls are disabled and say so, rather than being live and answering
   * a 403 afterwards — a rep who clicks Start and gets a red bar has learnt nothing except that
   * this screen cannot be trusted, and the next thing they do is telephone somebody.
   */
  const mayManage = canManageCampaigns(user.role);
  const gate = roleGate(
    mayManage,
    'Building, dry-running, starting and pausing a campaign need a role of manager or above',
    user.role,
  );
  const campaigns = useQuery({ queryKey: ['campaigns', 'list'], queryFn: () => client.list() });
  const facets = useQuery({ queryKey: ['campaigns', 'facets'], queryFn: () => client.facets() });

  const [selected, setSelected] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [draft, setDraft] = useState<CampaignDefinition | null>(null);
  const [dry, setDry] = useState<DryRun | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [pauseReason, setPauseReason] = useState('');

  const detail = useQuery({
    queryKey: ['campaigns', 'one', selected],
    queryFn: () => client.get(selected!),
    enabled: selected !== null,
  });

  const refresh = async (): Promise<void> => { await qc.invalidateQueries({ queryKey: ['campaigns'] }); };

  const create = useMutation({
    mutationFn: (d: CampaignDefinition) => client.create(d),
    onSuccess: async (c) => { setBuilding(false); setDraft(null); setSelected(c.id); setDry(null); setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });
  const runDry = useMutation({
    mutationFn: (id: string) => client.dryRun(id),
    onSuccess: async (r) => { setDry(r); setFailed(null); await refresh(); },
    onError: (e: Error) => { setDry(null); setFailed(e.message); },
  });
  const start = useMutation({
    mutationFn: (id: string) => client.start(id),
    onSuccess: async () => { setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });
  const pause = useMutation({
    mutationFn: (v: { id: string; reason: string }) => client.pause(v.id, v.reason),
    onSuccess: async () => { setPauseReason(''); setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });

  if (campaigns.isError) {
    return <ErrorState error={campaigns.error} context="open Campaigns" onRetry={() => void campaigns.refetch()} />;
  }
  if (!campaigns.data || !facets.data) {
    return <p className="p-5 text-sm text-fg-muted">Reading this tenant's campaigns…</p>;
  }

  const list = campaigns.data;
  const current = detail.data;

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-fg">Campaigns</h1>
            <p className="mt-1 max-w-2xl text-sm text-fg-muted">
              A campaign reaches thousands of customers at once and cannot be recalled. Nothing is sent
              until it has been dry-run, and the dry run shows the exact list, every exclusion by name,
              and the rupees.
            </p>
          </div>
          <Button
            disabled={!mayManage}
            title={gate}
            onClick={() => { setBuilding(true); setDraft(blank(facets.data.tenantLanguages)); setDry(null); setFailed(null); }}
          >
            New campaign
          </Button>
        </header>

        {gate && (
          <p role="status" className="rounded-[var(--h1-radius)] bg-neutral-soft px-3 py-2 text-xs text-neutral-soft-fg">
            {gate} Everything on this page is readable — what a campaign will cost and who it would
            reach is exactly what somebody asks a manager to check before it goes.
          </p>
        )}

        {failed && (
          <p role="alert" className="rounded-[var(--h1-radius)] border border-danger bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
            {failed}
          </p>
        )}

        {building && draft && (
          <Builder
            draft={draft}
            facets={facets.data}
            onChange={setDraft}
            onCancel={() => { setBuilding(false); setDraft(null); }}
            onSave={() => create.mutate(draft)}
            saving={create.isPending}
            gate={gate}
          />
        )}

        {list.length === 0 && !building ? (
          <EmptyState
            title="No campaigns yet"
            body="A campaign is a template, an audience, a cap, a window and a pace. Build one and dry-run it — nothing is sent until you have seen the list and the cost."
          />
        ) : (
          <section aria-label="Campaigns" className="flex flex-col gap-2">
            {list.map((c) => (
              <CampaignRow
                key={c.id}
                campaign={c}
                open={selected === c.id}
                onToggle={() => { setSelected(selected === c.id ? null : c.id); setDry(null); setFailed(null); }}
              />
            ))}
          </section>
        )}

        {current && selected && (
          <Detail
            detail={current}
            dry={dry}
            onDryRun={() => runDry.mutate(selected)}
            dryRunning={runDry.isPending}
            onStart={() => start.mutate(selected)}
            starting={start.isPending}
            onPause={() => pause.mutate({ id: selected, reason: pauseReason })}
            pausing={pause.isPending}
            pauseReason={pauseReason}
            setPauseReason={setPauseReason}
            gate={gate}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- the list */

function CampaignRow({ campaign, open, onToggle }: { campaign: Campaign; open: boolean; onToggle: () => void }): ReactNode {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`campaign-${campaign.id}`}
      className={clsx(
        'h1-card flex flex-wrap items-center gap-3 px-3.5 py-3 text-left transition-colors hover:border-line-strong',
        open && 'border-brand',
      )}
    >
      <Pill tone={STATUS_TONE[campaign.status] ?? 'neutral'} dot>{campaign.status}</Pill>
      <span className="font-medium text-fg">{campaign.name}</span>
      <span className="text-xs text-fg-subtle">{campaign.templateName}</span>
      <span className="ml-auto flex items-center gap-3 text-xs text-fg-muted tabular-nums">
        <span title="Messages per tenant-local day for this campaign">cap {campaign.dailyCap}/day</span>
        <span title="Meta's default throughput ceiling is 80/s; this is an upper bound, never a target">
          {campaign.pacePerSecond}/s
        </span>
        <span>{campaign.window.start}–{campaign.window.end}</span>
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------------- the builder */

function Builder({
  draft, facets, onChange, onCancel, onSave, saving, gate,
}: {
  draft: CampaignDefinition; facets: Facets;
  onChange: (d: CampaignDefinition) => void; onCancel: () => void; onSave: () => void; saving: boolean;
  /** Why this role may not save, or undefined when it may. Carried in from the page. */
  gate: string | undefined;
}): ReactNode {
  const set = <K extends keyof CampaignDefinition>(k: K, v: CampaignDefinition[K]): void => onChange({ ...draft, [k]: v });
  const toggle = (list: string[] | undefined, value: string): string[] => {
    const set_ = new Set(list ?? []);
    if (set_.has(value)) set_.delete(value); else set_.add(value);
    return [...set_];
  };

  return (
    <section className="h1-card flex flex-col gap-4 p-4" aria-label="New campaign">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg">Name</span>
          <input className={INPUT} value={draft.name} onChange={(e) => set('name', e.target.value)} aria-label="Campaign name" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg">Template</span>
          <input className={INPUT} value={draft.templateName} onChange={(e) => set('templateName', e.target.value)} aria-label="Template name" />
          <span className="text-xs text-fg-subtle">
            The name only. The language is resolved per contact — a Hindi customer gets the Hindi
            template or is excluded, never the English one.
          </span>
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg">Language for contacts who never chose one</span>
          <select className={INPUT} value={draft.defaultLanguage} onChange={(e) => set('defaultLanguage', e.target.value)} aria-label="Default language">
            {facets.tenantLanguages.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg">Daily cap</span>
          <input
            className={INPUT} type="number" min={1} max={facets.caps.maxDailyCap}
            value={draft.dailyCap} onChange={(e) => set('dailyCap', Number(e.target.value))} aria-label="Daily cap"
          />
          {/*
            The tier is named, not just its consequence. `max` above already clamps the field; what
            an operator cannot do is explain a clamp they were never told the reason for, and the
            one they invent ("the console is broken") costs a phone call. See `messagingTierNote`
            for why the tier word and the ceiling are two sentences rather than one.
          */}
          <span className="text-xs text-fg-subtle" data-testid="daily-cap-tier">
            {messagingTierNote(facets.caps)}
          </span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg">Pace (messages/second)</span>
          <input
            className={INPUT} type="number" min={0.1} step={0.1} max={facets.caps.maxPacePerSecond}
            value={draft.pacePerSecond} onChange={(e) => set('pacePerSecond', Number(e.target.value))} aria-label="Pace per second"
          />
          <span className="text-xs text-fg-subtle">
            A ceiling, never a target. Meta's default throughput limit is {facets.caps.maxPacePerSecond}/s.
          </span>
        </label>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-fg">Send window ({facets.timezone})</legend>
        <div className="flex flex-wrap items-center gap-2">
          {DAYS.map((d) => (
            <label key={d} className="flex items-center gap-1 text-xs text-fg-muted">
              <input
                type="checkbox" checked={draft.window.days.includes(d)}
                onChange={() => set('window', { ...draft.window, days: toggle(draft.window.days, d) as Weekday[] })}
                aria-label={d}
              />
              {d}
            </label>
          ))}
          <input
            className={clsx(INPUT, 'w-28')} type="time" value={draft.window.start}
            onChange={(e) => set('window', { ...draft.window, start: e.target.value })} aria-label="Window start"
          />
          <span className="text-sm text-fg-muted">to</span>
          <input
            className={clsx(INPUT, 'w-28')} type="time" value={draft.window.end}
            onChange={(e) => set('window', { ...draft.window, end: e.target.value })} aria-label="Window end"
          />
        </div>
        <p className="text-xs text-fg-subtle">
          Quiet hours are {facets.quietHours.start}–{facets.quietHours.end} and outrank this window. A
          window that runs into them is refused.
        </p>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-3">
        <Facet
          label="Tiers" options={facets.tiers} selected={draft.audience.tiers ?? []}
          onToggle={(v) => set('audience', { ...draft.audience, tiers: toggle(draft.audience.tiers, v) })}
        />
        <Facet
          label="Cities" options={facets.cities} selected={draft.audience.cities ?? []}
          onToggle={(v) => set('audience', { ...draft.audience, cities: toggle(draft.audience.cities, v) })}
        />
        <Facet
          label="Tags" options={facets.tags} selected={draft.audience.tags ?? []}
          onToggle={(v) => set('audience', { ...draft.audience, tags: toggle(draft.audience.tags, v) })}
        />
      </div>

      <Facet
        label="Consent source" options={facets.consentSources} selected={draft.audience.consentSources ?? []}
        onToggle={(v) => set('audience', { ...draft.audience, consentSources: toggle(draft.audience.consentSources, v) })}
      />

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-fg">City sequence</span>
        <input
          className={INPUT} placeholder="Mumbai, Delhi, Bengaluru"
          value={draft.citySequence.join(', ')}
          onChange={(e) => set('citySequence', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          aria-label="City sequence"
        />
        <span className="text-xs text-fg-subtle">Each city is exhausted before the next one starts.</span>
      </label>

      <ParamEditor
        params={draft.params}
        onChange={(p) => set('params', p)}
      />

      <div className="flex items-center gap-2">
        <Button
          onClick={onSave}
          disabled={saving || Boolean(gate) || !draft.name.trim() || !draft.templateName.trim()}
          title={gate}
          data-testid="save-draft"
        >
          {saving ? 'Saving…' : 'Save as draft'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <span className="text-xs text-fg-subtle">Saving does not send anything. The next step is the dry run.</span>
      </div>
    </section>
  );
}

function Facet({
  label, options, selected, onToggle,
}: { label: string; options: { value: string; n: number }[]; selected: string[]; onToggle: (v: string) => void }): ReactNode {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-sm font-medium text-fg">{label}</legend>
      {options.length === 0 ? (
        <p className="text-xs text-fg-subtle">Nothing recorded yet — this filter would match nobody.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              aria-pressed={selected.includes(o.value)}
              className={clsx(
                'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                selected.includes(o.value) ? 'border-brand bg-brand-soft text-brand-soft-fg' : 'border-line text-fg-muted hover:border-line-strong',
              )}
            >
              {o.value} <span className="tabular-nums opacity-70">{o.n}</span>
            </button>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function ParamEditor({ params, onChange }: { params: Record<string, ParamSpec>; onChange: (p: Record<string, ParamSpec>) => void }): ReactNode {
  const keys = Object.keys(params).sort((a, b) => Number(a) - Number(b));
  const next = String(keys.length + 1);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-fg">Template parameters</legend>
      {keys.map((k) => {
        const spec = params[k]!;
        return (
          <div key={k} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-12 font-mono text-xs text-fg-muted">{`{{${k}}}`}</span>
            <select
              className={INPUT}
              value={spec.source}
              aria-label={`Parameter ${k} source`}
              onChange={(e) => {
                const source = e.target.value as ParamSpec['source'];
                const replaced: ParamSpec = source === 'literal'
                  ? { source, value: '' }
                  : source === 'lead.fact'
                    ? { source, key: '', fallback: '' }
                    : { source, fallback: '' };
                onChange({ ...params, [k]: replaced });
              }}
            >
              <option value="contact.name">Their name</option>
              <option value="contact.city">Their city</option>
              <option value="lead.fact">A recorded fact</option>
              <option value="literal">Fixed text</option>
            </select>
            {spec.source === 'lead.fact' && (
              <input
                className={INPUT} placeholder="fact key" value={spec.key} aria-label={`Parameter ${k} fact key`}
                onChange={(e) => onChange({ ...params, [k]: { ...spec, key: e.target.value } })}
              />
            )}
            <input
              className={INPUT}
              placeholder={spec.source === 'literal' ? 'text' : 'fallback'}
              aria-label={`Parameter ${k} ${spec.source === 'literal' ? 'text' : 'fallback'}`}
              value={spec.source === 'literal' ? spec.value : (spec.fallback ?? '')}
              onChange={(e) => onChange({
                ...params,
                [k]: spec.source === 'literal' ? { source: 'literal', value: e.target.value } : { ...spec, fallback: e.target.value },
              })}
            />
            <Button variant="ghost" size="sm" onClick={() => {
              const rest = { ...params };
              delete rest[k];
              onChange(rest);
            }}>Remove</Button>
          </div>
        );
      })}
      <div>
        <Button variant="secondary" size="sm" onClick={() => onChange({ ...params, [next]: { source: 'contact.name', fallback: '' } })}>
          Add parameter
        </Button>
      </div>
      <p className="text-xs text-fg-subtle">
        A parameter with no value and no fallback excludes that contact rather than sending a visible
        placeholder — Meta refuses a template whose parameters do not match its definition.
      </p>
    </fieldset>
  );
}

/* ---------------------------------------------------------------------- the detail */

function Detail({
  detail, dry, onDryRun, dryRunning, onStart, starting, onPause, pausing, pauseReason, setPauseReason, gate,
}: {
  detail: { campaign: Campaign; lastDryRun: { current: boolean; ranBy: string; at: string; eligible: number; costInr: number | null } | null; progress: Progress | null };
  dry: DryRun | null;
  onDryRun: () => void; dryRunning: boolean;
  onStart: () => void; starting: boolean;
  onPause: () => void; pausing: boolean;
  pauseReason: string; setPauseReason: (v: string) => void;
  /** Why this role may not dry-run, start or pause, or undefined when it may. */
  gate: string | undefined;
}): ReactNode {
  const c = detail.campaign;
  return (
    <section className="flex flex-col gap-4" aria-label={`${c.name} detail`}>
      <div className="h1-card flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-fg">{c.name}</h2>
          <Pill tone={STATUS_TONE[c.status] ?? 'neutral'} dot>{c.status}</Pill>
          <span className="ml-auto flex gap-2">
            <Button
              variant="secondary" onClick={onDryRun} disabled={dryRunning || Boolean(gate)}
              title={gate} data-testid="dry-run"
            >
              {dryRunning ? 'Reading the audience…' : 'Dry run'}
            </Button>
            {c.status === 'running' ? (
              <>
                <input
                  className={clsx(INPUT, 'w-56')} placeholder="Why are you stopping it?"
                  value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} aria-label="Pause reason"
                  disabled={Boolean(gate)} title={gate}
                />
                <Button
                  variant="danger" onClick={onPause} disabled={pausing || Boolean(gate)}
                  title={gate} data-testid="pause"
                >
                  {pausing ? 'Stopping…' : 'Pause'}
                </Button>
              </>
            ) : (
              <Button
                onClick={onStart} disabled={!c.startable || starting || Boolean(gate)}
                title={gate} data-testid="start"
              >
                {starting ? 'Starting…' : 'Start sending'}
              </Button>
            )}
          </span>
        </div>
        {!c.startable && c.status !== 'running' && (
          <p className="text-sm text-warn-soft-fg" data-testid="not-startable">{c.notStartableBecause}</p>
        )}
        {detail.lastDryRun && !detail.lastDryRun.current && (
          <p className="text-sm text-warn-soft-fg">
            The last dry run was of an earlier version of this campaign. Run it again — the list and
            the cost have both moved.
          </p>
        )}
      </div>

      {dry && <DryRunPanel dry={dry} startable={c.startable} />}
      {detail.progress && detail.progress.enqueued > 0 && <Results progress={detail.progress} />}
    </section>
  );
}

function DryRunPanel({ dry, startable }: { dry: DryRun; startable: boolean }): ReactNode {
  const excluded = useMemo(
    () => Object.entries(dry.counts).sort((a, b) => b[1] - a[1]),
    [dry.counts],
  );
  const deferred = useMemo(
    () => Object.entries(dry.deferrals).filter(([, n]) => n > 0),
    [dry.deferrals],
  );

  return (
    <section className="h1-card flex flex-col gap-4 p-4" aria-label="Dry run" data-testid="dry-run-result">
      <div className="flex flex-wrap gap-4">
        <Figure label="Matched the filter" value={String(dry.candidates)} />
        <Figure label="Will be sent" value={String(dry.eligible.length)} brand />
        <Figure label="Goes out today" value={String(dry.schedule.today)} />
        <Figure
          label="Cost, tax included"
          value={rupees(dry.cost.inr)}
          note={dry.cost.unknown > 0 ? `${dry.cost.unknown} could not be priced — counted, not treated as free` : `${rupees(dry.cost.perMessageInr)} per message`}
        />
        <Figure label="Cost today" value={rupees(dry.cost.todayInr)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-fg">Not sent, and why</h3>
          {excluded.length === 0 ? (
            <p className="mt-1 text-sm text-fg-muted">Nobody in this audience was excluded.</p>
          ) : (
            <ul className="mt-1.5 flex flex-col gap-1" data-testid="exclusions">
              {excluded.map(([reason, n]) => (
                <li key={reason} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-fg-muted">{exclusionLabel(reason)}</span>
                  <span className="tabular-nums font-medium text-fg">{n}</span>
                </li>
              ))}
            </ul>
          )}
          {deferred.length > 0 && (
            <>
              <h3 className="mt-3 text-sm font-medium text-fg">Not today — they go later</h3>
              <ul className="mt-1.5 flex flex-col gap-1">
                {deferred.map(([reason, n]) => (
                  <li key={reason} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-fg-muted">{exclusionLabel(reason)}</span>
                    <span className="tabular-nums font-medium text-fg">{n}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium text-fg">One template per language</h3>
          <ul className="mt-1.5 flex flex-col gap-1" data-testid="by-language">
            {dry.byLanguage.map((b) => (
              <li key={`${b.language}-${b.templateLanguage}`} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-fg-muted">
                  {b.language} → <span className="font-mono text-xs">{b.template} ({b.templateLanguage})</span>
                </span>
                <span className="tabular-nums text-fg">
                  {b.count}
                  {b.assumed > 0 && (
                    <span className="ml-1 text-[11px] text-fg-subtle" title="These contacts have never had a conversation, so they get the campaign's declared default language">
                      ({b.assumed} assumed)
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <h3 className="mt-3 text-sm font-medium text-fg">Schedule</h3>
          {/*
            The send window is defined in the TENANT's timezone — the builder above even prints it
            beside the day and hour controls — and these two read-outs used the viewer's instead.
            On a laptop set to UTC "closing 19:00" showed as "closing 13:30", which is the one
            number somebody schedules a campaign around. `formatTime` pins Asia/Kolkata, and the
            zone is named beside it because a bare clock time with no zone is the whole bug. The
            null checks replace a `?? ''` that rendered "Invalid Date" if the shape ever changed.
          */}
          <p className="mt-1 text-sm text-fg-muted">
            The window is {dry.schedule.window.open ? 'open' : `shut — ${WINDOW_REASONS[dry.schedule.window.reason]}`}
            {dry.schedule.window.open && dry.schedule.window.closesAt !== null
              ? `, closing ${formatTime(dry.schedule.window.closesAt)} IST`
              : !dry.schedule.window.open && dry.schedule.window.opensAt !== null
                ? `, opening ${formatDateTime(dry.schedule.window.opensAt)}`
                : ''}
            . Cap {dry.schedule.dailyCap}/day, {dry.schedule.sentToday} already sent today,
            {' '}{dry.schedule.pacePerSecond}/s — about {dry.schedule.estimatedMinutes}{' '}
            {dry.schedule.estimatedMinutes === 1 ? 'minute' : 'minutes'} for today's batch.
          </p>
        </div>
      </div>

      <div className="rounded-[var(--h1-radius)] border border-warn bg-warn-soft px-3 py-2.5" data-testid="divergence">
        <h3 className="text-sm font-medium text-warn-soft-fg">
          Before you press Start — how the live send can still differ from this
        </h3>
        <ul className="mt-1.5 list-disc pl-5 text-sm text-warn-soft-fg">
          {dry.divergence.map((d) => <li key={d}>{d}</li>)}
        </ul>
      </div>

      {!startable && (
        <p className="text-sm text-fg-muted">
          This dry run has been recorded. Reload the campaign to arm it.
        </p>
      )}
      {dry.listTruncated && (
        <p className="text-sm text-fg-muted">
          The stored list was truncated: this audience is larger than the comparison cap, so the
          divergence report will cover only the first part of it.
        </p>
      )}
    </section>
  );
}

function Results({ progress }: { progress: Progress }): ReactNode {
  return (
    <section className="h1-card flex flex-col gap-4 p-4" aria-label="Results" data-testid="results">
      <div className="flex flex-wrap gap-4">
        <Figure label="Committed" value={String(progress.enqueued)} />
        <Figure label="Sent" value={String(progress.sent)} />
        <Figure label="Failed" value={String(progress.failed)} />
        <Figure
          label="In flight"
          value={String(progress.inFlight)}
          note="Already with the sender — a pause cannot recall these"
        />
        {progress.cancelled > 0 && (
          <Figure
            label="Recalled"
            value={String(progress.cancelled)}
            note="Queued, then stopped before they were sent — never charged"
          />
        )}
        <Figure
          label="Spent"
          value={rupees(progress.costInr)}
          note={progress.costUnknown > 0 ? `${progress.costUnknown} rows have no price on them` : undefined}
        />
        <Figure
          label="Replies"
          value={String(progress.replies)}
          note={`${progress.leads} new ${progress.leads === 1 ? 'lead' : 'leads'}`}
        />
        {progress.remaining !== null && <Figure label="Still to come" value={String(progress.remaining)} />}
      </div>

      {progress.pausedAt && (
        <p className="text-sm text-fg-muted">
          Paused by {progress.pausedBy} at {formatDateTime(progress.pausedAt)}
          {progress.pauseReason ? ` — "${progress.pauseReason}"` : ''}. It had committed {progress.enqueued}{' '}
          {progress.enqueued === 1 ? 'message' : 'messages'} costing {rupees(progress.costInr)}.{' '}
          {progress.cancelled} queued {progress.cancelled === 1 ? 'message was' : 'messages were'} recalled before
          they were sent; {progress.inFlight} {progress.inFlight === 1 ? 'was' : 'were'} already with the sender and
          could not be.
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-fg">Failures, by whose they are</h3>
          <p className="mt-1 text-xs text-fg-subtle">
            Meta's refusals are about the recipient and stand that contact down; ours are about the
            payload or the configuration and no amount of waiting fixes them.
          </p>
          <ul className="mt-1.5 flex flex-col gap-1" data-testid="by-side">
            <li className="flex items-baseline justify-between text-sm">
              <span className="text-fg-muted">Meta's</span>
              <span className="tabular-nums font-medium text-fg">{progress.bySide.meta}</span>
            </li>
            <li className="flex items-baseline justify-between text-sm">
              <span className="text-fg-muted">Ours</span>
              <span className="tabular-nums font-medium text-fg">{progress.bySide.ours}</span>
            </li>
          </ul>
          <ul className="mt-2 flex flex-col gap-1">
            {progress.byCode.map((c) => (
              <li key={c.code} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="text-fg-muted">
                  <span className="font-mono">{c.code}</span> · {c.label}
                </span>
                <span className="tabular-nums text-fg">{c.n}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-medium text-fg">Refused before sending</h3>
          {progress.refusals.length === 0 ? (
            <p className="mt-1 text-sm text-fg-muted">Nothing was refused.</p>
          ) : (
            <ul className="mt-1.5 flex flex-col gap-1" data-testid="refusals">
              {progress.refusals.map((r) => (
                <li key={r.reason} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-fg-muted">{exclusionLabel(r.reason)}</span>
                  <span className="tabular-nums font-medium text-fg">{r.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function Figure({ label, value, note, brand }: { label: string; value: string; note?: string; brand?: boolean }): ReactNode {
  return (
    <div
      className="flex min-w-[8rem] flex-col gap-0.5"
      data-testid={`figure-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <span className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">{label}</span>
      {/*
        `text-brand`, not `text-brand-ink`: at text-2xl/600 this is large text under WCAG 1.4.3,
        which asks 3:1 — the brand orange measures 3.30:1 on a card and clears it. Shrink this
        number and it becomes body text, and it has to move to `text-brand-ink`.
      */}
      <span className={clsx('text-2xl font-semibold tabular-nums', brand ? 'text-brand' : 'text-fg')}>{value}</span>
      {note && <span className="text-[11px] text-fg-subtle">{note}</span>}
    </div>
  );
}
